import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import { BookingSummary } from "../../../../../types/bookingCreation";
import {
  selectAssetGroupById,
  selectLocationById,
} from "../../../../../redux/slice/booking/bookingSlice";
import moment from "moment";
import BookingStatus from "./BookingStatus";
import { getBookingStatusType, getEventType } from "../../../../../util";
import { ServiceTypeSelectorOption } from "../../../../../types/order";
import { i18n } from "../../../../../i18n/i18n";

type BookingSummaryCardProps = {
  bookingSummaryData: BookingSummary;
  rentalExpired: boolean;
  amount?: number;
  serviceType: string;
};
function BookingSummaryCard({
  bookingSummaryData,
  rentalExpired,
  amount = 1,
  serviceType,
}: BookingSummaryCardProps) {
  const { t } = useTranslation();

  // Defensive extraction of first item (most code assumed index 0)
  const firstItem = bookingSummaryData?.items?.[0];
  const groupId = firstItem?.groupId;
  const startDate = firstItem?.fromDate;
  const endDate = firstItem?.toDate;
  const eventType = firstItem?.metadata?.eventType;

  const bookedItem = useSelector((state: RootState) =>
    selectAssetGroupById(state, groupId as any)
  );
  const location = useSelector((state: RootState) =>
    selectLocationById(state, bookingSummaryData?.locationId)
  );

  const currentLanguage = i18n.language;
  const recipientData = bookingSummaryData?.recipientData;
  const service = (bookingSummaryData?.service || "").toLowerCase();
  const isApartment = service === ServiceTypeSelectorOption.APARTMENT;
  const isVenue = serviceType === ServiceTypeSelectorOption.VENUE;
  const hideAssignedLockerRow =
    isApartment || service === ServiceTypeSelectorOption.VENUE;

  // Resolve localized names (prefer names carried with booking to ensure snapshot consistency)
  const langNames = firstItem?.group?.langNames || bookedItem?.langNames;
  const bookedItemName = firstItem?.group?.name || bookedItem?.name;

  // Memoize formatted booked item name to avoid recomputation on every render
  const formattedBookedItemName = useMemo(() => {
    if (!bookedItem) return "";
    if (!currentLanguage) return "";

    const localizedTitle = langNames?.[currentLanguage]?.title;

    switch (bookingSummaryData.service) {
      case ServiceTypeSelectorOption.P2P:
      case ServiceTypeSelectorOption.STORAGE:
        return `${amount}x ${t(
          "orders.ticket_overview.booked_ticket.locker"
        )} - ${t("orders.ticket_overview.booked_ticket.size")} ${
          localizedTitle ?? bookedItemName ?? ""
        }`.trim();
      case ServiceTypeSelectorOption.SUP: {
        const count = bookingSummaryData?.items?.length || 0;
        return `${count} x ${localizedTitle ?? bookedItemName ?? ""}`.trim();
      }
      default:
        return localizedTitle ?? bookedItemName ?? "";
    }
  }, [
    bookedItem,
    currentLanguage,
    langNames,
    bookingSummaryData,
    amount,
    bookedItemName,
    t,
  ]);

  if (!bookedItem) return null; // Still guard – cannot display without asset group

  const textBoldClass =
    "text-foreground/80 font-bold leading-[20px] tracking-[0.02em] break-words";

  const renderTime = (date: any, isStart: boolean) => {
    if (!date) return "--";
    if (isApartment) return isStart ? "15:00" : "11:00"; // Apartment fixed times
    return moment(date).format("LT");
  };

  const bookingStatus = getBookingStatusType(
    firstItem?.rental,
    rentalExpired,
    serviceType
  );

  return (
    <div className="bg-background flex flex-col pt-8 pb-8 px-5 border border-foreground/10 relative min-h-[330px]">
      <div className="absolute right-0 top-[29%] w-[5px] h-[10px] bg-primary rounded-l-full " />
      <div className="absolute left-0 top-[29%] w-[5px] h-[10px] bg-primary  rounded-r-full" />

      <div className="flex flex-row justify-end text-body2">
        <BookingStatus status={bookingStatus} />
      </div>

      <div className="flex justify-between mt-2">
        <div className="text-left">
          <p>{t("orders.ticket_overview.start")}</p>
          <p className={textBoldClass}>
            {startDate ? moment(startDate).format("L") : "--"}
          </p>
          <p className={textBoldClass}>{renderTime(startDate, true)}</p>
        </div>
        <div className="text-right">
          <p>{t("orders.ticket_overview.end")}</p>
          <p className={textBoldClass}>
            {endDate ? moment(endDate).format("L") : "--"}
          </p>
          <p className={textBoldClass}>{renderTime(endDate, false)}</p>
        </div>
      </div>

      <div className="mt-1">
        <p>{t("orders.ticket_overview.location")}</p>
        {isVenue ? (
          <p className={textBoldClass}>{formattedBookedItemName}</p>
        ) : (
          <p className={textBoldClass}>{location?.name ?? "--"}</p>
        )}
      </div>

      {bookingSummaryData.service === ServiceTypeSelectorOption.P2P &&
        recipientData && (
          <div className="mt-1">
            <p>{t("orders.ticket_overview.recipient_details")}</p>
            <p className={textBoldClass}>
              {recipientData?.firstname}
              {recipientData?.lastname ? ` ${recipientData.lastname}` : ""}
            </p>
            <p className={textBoldClass}>{recipientData?.email}</p>
            <p className={textBoldClass}>{recipientData?.telephone}</p>
          </div>
        )}

      <div className="mt-1">
        <p>
          {isApartment
            ? t("orders.ticket_overview.apartment")
            : t("orders.ticket_overview.booked_items")}
        </p>
        {isVenue ? (
          <p className={textBoldClass}>
            {eventType && (
              <>
                {t("orders.ticket_overview.venue")} -{" "}
                {getEventType(eventType, i18n.language as "en" | "de")}
              </>
            )}
          </p>
        ) : (
          <p className={textBoldClass}>{formattedBookedItemName}</p>
        )}
      </div>

      <div className="mt-1">
        <p className="!text-base">
          {t("orders.ticket_overview.booking_number")}
        </p>
        <p className={textBoldClass}>{bookingSummaryData.bookingNumber}</p>
      </div>

      {!hideAssignedLockerRow && (
        <div className="mt-1">
          <p className="!text-base">
            {t("orders.ticket_overview.assigned_locker")}
          </p>
          <p className={textBoldClass}>
            {firstItem?.rental?.doors?.[0]?.door ?? "--"}
          </p>
        </div>
      )}
    </div>
  );
}

export default BookingSummaryCard;
